/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { FC, useCallback, useEffect, useRef, useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NextRouter, useRouter } from 'next/router';
import { sprintf } from 'sprintf-js';
import classNames from 'classnames';

import Button from 'components/atoms/Button';
import ProCTALink from 'components/atoms/ProCTALink';
import JournalGroup from 'components/molecules/JournalGroup';
import Supporters from 'modules/marketing-pages/components/LandingPage/Supporters';
import ProBlogPostList from 'components/molecules/ProBlogPostList';
import Breadcrumbs from 'modules/common/components/Breadcrumbs';
import InternalLinkGroups from 'modules/common/components/InternalLinkGroups';
import type { VerticalPageJobType } from 'modules/job-types/types';
import type {
  ProBlogPost,
  ProFeedback,
  BlogPost,
  UpdateFiltersParams,
} from 'modules/marketing-pages/marketing-page.types';
import BannerSticky from 'modules/marketing-pages/components/Banners/BannerSticky';
import BannerZipcode from 'modules/marketing-pages/components/Banners/BannerZipcode';
import ListProsContainer, {
  DEFAULT_PAGE,
  NUM_PROS_PER_PAGE,
} from 'modules/marketing-pages/components/ListPros';
import type { ListProsProps } from 'modules/marketing-pages/components/ListPros';
import FindProGroup from 'modules/marketing-pages/components/LandingPage/FindProGroup';
import VerticalPopularRequest from 'modules/marketing-pages/components/LandingPage/VerticalPopularRequest';
import VerticalFAQ from 'modules/marketing-pages/components/LandingPage/VerticalFAQ';
import ListRecentReviews from 'modules/marketing-pages/components/LandingPage/ListRecentReviews';
import ListCaseStudies from 'modules/marketing-pages/components/ListCaseStudies';
import type { ListCaseStudiesProps } from 'modules/marketing-pages/components/ListCaseStudies';
import {
  getCaseStudies,
  listPros,
  getPopularRequests,
  updateFiltersAndListNewPros,
} from 'modules/marketing-pages/duck';
import { makeSelectTranslations } from 'modules/translations/duck';
import { openNewRequestModal } from 'modules/requests/service';

import styles from './LandingPage.module.scss';
import {
  caseStudiesTotalCountSelector,
  popularRequestsSelector,
  proCountGroupSelector,
  proCountSelector,
} from 'modules/marketing-pages/duck/selectors';
import { MarketingPageContext } from 'src/context/marketing-page';
import VerticalProQuestionAndAnswers from './VerticalProQuestionAndAnswers';
import VerticalServiceFlow from './VerticalServiceFlow';
import CalendarInverted from 'images/icons/calendar-inverted.svg';
import Calendar from 'images/icons/calendar.svg';
import IconRight from 'images/icons/Icon_Right.svg';

import type { PopularRequest } from 'modules/marketing-pages/service';
import PillFilters from 'components/molecules/PillFilters';
import type { RootState } from 'store/types';

import {
  MATCHING_CRITERIA_QUERY_PARAM_KEY,
  PAGE_QUERY_PARAM_KEY,
  PRICE_RANGE_QUERY_PARAM_KEY,
  getQueryParamFromUrlSearchParamsForKey,
  setupMatchingCriteriaFilterToggleItems,
  setupPricingFilters,
} from 'modules/marketing-pages/utils';

import { PillType, DropdownItemTypes } from 'components/molecules/PillFilters/PillFilters.types';

import {
  CostAndTime,
  COST_AND_TIME_INFO_POSITION_ABOVE_FINDPROGROUP,
  COST_AND_TIME_INFO_POSITION_ABOVE_VERTICALFAQ,
  COST_AND_TIME_INFO_POSITION_UNDER_SUMMARY,
} from './CostAndTime/CostAndTime';

import { utmParamPairsForSimplifiedLayout as utmParamPairs } from 'utils/constant';

import { useSocialRequestContinuation } from 'src/hooks';

const REMOTE_JOBTYPES = ['yoga-instructor', 'english-teacher', 'personal-trainer'];
const DAY_TIME_KEY = 'DateTime'; // change everywhere to dayTime, or split

const handleClientsideFilterRouting = (router: NextRouter, url: URL) => {
  const urlToDisplay = `${url.pathname}${url.search}`;
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({ pathname: '/vertical' }, urlToDisplay, {
    shallow: true,
  });
};

interface City {
  _id: string;
  id: string;
  key: string;
  name: string;
}
interface Prefecture {
  _id: string;
  id: string;
  key: string;
  name: string;
}

interface UtmParameter {
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
}

export const getAsPathURLString = (asPath: string) =>
  new URL(asPath, process.env.DOMAIN ?? 'https://zehitomo.com').toString();

export interface Props {
  articleEnabled: boolean;
  cities?: Array<City>;
  city?: City;
  cityGroup?: string;
  cityGroupName?: string;
  crumbs: Array<{ link: string; text: string }>;
  currentCaseStudiesPage: number;
  day?: string;
  featureFlags: { isReviewsQAEnabled: boolean };
  groupLink?: string;
  groups: Array<{ links: Array<{ route: string; text: string }>; title: string }>;
  isFirstPage: boolean;
  jobType: VerticalPageJobType;
  testVariant: string | null | { isInitialized: boolean; variant: string | null };
  newestReviews: Array<ProFeedback>;
  numOfCityPros: number;
  numOfGroupPros: number;
  onCaseStudiesPageChange: (currentPage: number) => void;
  online?: boolean;
  pageTranslationId: string;
  popularJobTypes: any;
  posts: Array<BlogPost>;
  prefecture?: Prefecture;
  proBlogPosts: Array<ProBlogPost>;
  proPageCount: number;
  time?: string;
  title: string;
  updateProPageCount: (nextProPageCount: number) => void;
  prefectures?: Array<Prefecture>;
}

const LandingPageDefault: FC<Props> = ({
  city,
  cityGroup,
  cityGroupName,
  numOfCityPros,
  numOfGroupPros,
  featureFlags,
  groupLink,
  crumbs,
  groups,
  jobType,
  newestReviews,
  pageTranslationId,
  posts,
  prefecture,
  proBlogPosts,
  proPageCount,
  currentCaseStudiesPage,
  updateProPageCount,
  onCaseStudiesPageChange,
  isFirstPage,
  testVariant,
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const dispatch = useDispatch();
  const { key: cityKey } = city ?? {};
  const { key: prefectureKey } = prefecture ?? {};
  const isVerticalTop = !prefecture?.key && !city?.key;
  const { id: jobTypeId, faqs, forms } = jobType;
  const lastContainerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const totalCaseStudies = useSelector(caseStudiesTotalCountSelector);
  const selectTranslations = useMemo(makeSelectTranslations, []);
  const translation = useSelector((state) =>
    selectTranslations(state as RootState, { translationId: pageTranslationId })
  );
  const asPathURLString = useMemo(() => getAsPathURLString(router.asPath), [router.asPath]);
  const filtersFocusElement = useRef<HTMLDivElement>(null);

  const popularRequests: Array<PopularRequest> = useSelector(popularRequestsSelector);
  const { internalBlogPosts } = useContext(MarketingPageContext);
  const journalGroupPosts = internalBlogPosts.length > 0 ? internalBlogPosts : posts;

  // for backward compatibility and cache / v0 cases
  const howToProvideService = useMemo(() => {
    const { fields } = forms.find((field) => field.name === 'howToProvideService') ?? {};
    const defaultHowToProvideService = { remote: true };
    if (fields) {
      const [howToProvideServiceField] = fields;
      switch (howToProvideServiceField.type) {
        case 'radio':
          return 'remote';
        case 'checkboxes':
        default:
          return defaultHowToProvideService;
      }
    }
    return defaultHowToProvideService;
  }, [forms]);

  const handlePriceRangeFilterValueChanged = useCallback(
    (data: string) => {
      const url = new URL(asPathURLString);
      url.searchParams.delete(PAGE_QUERY_PARAM_KEY);
      if (data === DropdownItemTypes.Unselect) {
        url.searchParams.delete(PRICE_RANGE_QUERY_PARAM_KEY);
      } else {
        url.searchParams.set(PRICE_RANGE_QUERY_PARAM_KEY, data);
      }
      handleClientsideFilterRouting(router, url);
    },
    [asPathURLString, router]
  );

  const handleNoProResult = useCallback(() => {
    const url = new URL(asPathURLString);
    const { searchParams } = url;

    searchParams.delete(PAGE_QUERY_PARAM_KEY);
    searchParams.delete(PRICE_RANGE_QUERY_PARAM_KEY);
    searchParams.delete(MATCHING_CRITERIA_QUERY_PARAM_KEY);
    searchParams.delete('day');
    searchParams.delete('time');
    handleClientsideFilterRouting(router, url);
  }, [asPathURLString, router]);

  const baseLandingPageProParameters = useMemo(
    () => ({
      cityGroup,
      cityKey,
      excludeCities: [cityKey],
      jobTypeId,
      language,
      prefectureKey,
      size: NUM_PROS_PER_PAGE,
      page: DEFAULT_PAGE,
    }),
    [cityKey, jobTypeId, language, prefectureKey]
  );

  const handleLoadPros = useCallback<ListProsProps['onLoadMore']>(
    ({ page, size, skip, day, time, priceRange, matchingCriteria }) => {
      dispatch(
        listPros({
          ...baseLandingPageProParameters,
          day,
          matchingCriteria,
          page,
          priceRange,
          size,
          skip,
          time,
        })
      );
    },
    [baseLandingPageProParameters, dispatch]
  );

  const handleLoadProsGroup = useCallback<ListProsProps['onLoadMore']>(
    ({ size }) => {
      dispatch(
        listPros({
          cityGroup,
          cityKey,
          jobTypeId,
          language,
          page: 1,
          prefectureKey,
          pricingMenuItemKey: '',
          size,
          skip: size,
        })
      );
    },
    [cityKey, dispatch, jobTypeId, language, prefectureKey]
  );

  const handleLoadMoreCaseStudies = useCallback<ListCaseStudiesProps['onLoadMore']>(
    ({ newPage }) => {
      dispatch(
        getCaseStudies({
          cityKey,
          currentPage: newPage,
          jobTypeId,
          prefectureKey,
        })
      );
    },
    [cityKey, dispatch, jobTypeId, prefectureKey]
  );

  const openRequestModalForRemoteJobs = useCallback(
    () =>
      openNewRequestModal({
        context: {
          city: { key: undefined, name: undefined },
          prefecture: {
            key: undefined,
            name: undefined,
          },
        },
        dispatch,
        properties: {
          initialValues: { howToProvideService },
          jobTypeId,
          stepsToSkipOnFirstTime: ['howToProvideService'],
        },
      }),
    [dispatch, howToProvideService, jobTypeId]
  );

  const handleProBlogButton = useCallback(() => {
    const langPath = language === 'en' ? '/en' : '';
    window.location.href = `${langPath}/services/${jobType.id}/pro-blog-link`;
  }, [language, jobType]);

  useEffect(() => {
    dispatch(
      getPopularRequests({
        cityKey: city?.key,
        jobTypeId: jobType.id,
        prefectureKey: prefecture?.key,
      })
    );
  }, [city, jobType, prefecture]);

  const requestFormQueryParam = router.query['request-form'] as string;
  const utmParameter: UtmParameter = {
    utmMedium: router.query.utm_medium as string,
    utmSource: router.query.utm_source as string,
    utmCampaign: router.query.utm_campaign as string,
  };

  const isBottomAreaShown = !utmParamPairs.some(
    (pair) => pair.utmMedium === utmParameter.utmMedium && pair.utmSource === utmParameter.utmSource
  );

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const jobTypeName = jobType.translations.seoName || jobType.translations.requestType;

  const isStartOnlineRequestShown = REMOTE_JOBTYPES.includes(jobType.id);

  const summary = jobType.seo?.landingPage?.summary;

  const costAndTimeInfoSummary = jobType.seo?.landingPage?.costAndTimeInfo?.summary;

  const shouldDisplayCTInfoSummary =
    jobType.seo?.landingPage?.costAndTimeInfo?.shouldDisplaySummary;

  const CTTableInfo = jobType.seo?.landingPage?.costAndTimeInfo?.tableInfo;

  const costAndTimeInfoPosition = jobType.seo?.landingPage?.costAndTimeInfo?.tableInfoPosition;

  const stickyRef = useRef<HTMLDivElement>(null);

  const [isSticky, setIsSticky] = useState<boolean>(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.boundingClientRect.y < 0) {
          setIsSticky(true);
        } else if (isSticky && entry.intersectionRatio > 0) {
          setIsSticky(false);
        }
      },
      { threshold: [1] }
    );
    if (stickyRef.current) observer.observe(stickyRef.current);
    return () => observer.disconnect();
  }, [isSticky]);

  //   const pref = (prefecture?.name ?? '') + cityGroupName;
  const title = sprintf(translation.prosTitle, {
    city: city?.name ?? '',
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    jobType: jobType.translations.seoName || jobType.translations.requestType,
    prefecture: (prefecture?.name ?? '') + (cityGroupName ?? ''),
    topProsNumber: 10,
  });

  const handlePillFilterClicked = useCallback(
    (key: string, value: Array<string>) => {
      if (!key || !value.length) return;

      const url = new URL(asPathURLString);
      const { searchParams } = url;

      if (key === DAY_TIME_KEY) {
        const [day, time] = value;
        url.searchParams.delete(PAGE_QUERY_PARAM_KEY);
        day ? searchParams.set('day', day) : searchParams.delete('day');
        time ? searchParams.set('time', time) : searchParams.delete('time');
        handleClientsideFilterRouting(router, url);
      }
    },
    [asPathURLString, router]
  );

  const getUrlDayTimeData = (dayUrlVal: string, timeUrlVal: string) => ({
    id: DAY_TIME_KEY,
    isToggleSelected: Boolean(dayUrlVal || timeUrlVal),
    key: DAY_TIME_KEY,
    // For this pill type,  key and value are not being used
    value: `${dayUrlVal},${timeUrlVal}`,
    link: false,
    pillText: '時間',
    pillIcon:
      dayUrlVal || timeUrlVal ? (
        <CalendarInverted className={styles['day-time-calendar-icon']} />
      ) : (
        <Calendar className={styles['day-time-calendar-icon']} />
      ),
    type: PillType.DayTime,
  });

  // Really we want to know if this is a normal 'city' request or a request for a city ander grouping
  const cityName = city?.name;
  const isGroupCity = cityGroupName !== undefined && cityName !== undefined;
  const groupPageTitle = cityGroupName !== undefined && cityName === undefined ? cityGroupName : '';

  const verticalProQuestionAndAnswerData = jobType.verticalQuestionAndAnswersToPro;
  const shouldShowVerticalHaveVerticalProQuestions =
    verticalProQuestionAndAnswerData?.supportedLanguages.includes(language) &&
    isFirstPage &&
    isVerticalTop;

  // Only show Vertical Service flow in vertical top, first page, and when the job type has the service flow with the correct selcted language.
  const verticalServiceFlowData = jobType.verticalServiceFlow;
  const jobTypeVerticalServiceFlowSteps = verticalServiceFlowData
    ? verticalServiceFlowData.steps
    : [];
  // Only show Vertical Service flow in vertical top, first page, has the service flow steps and when the job type has the service flow with the correct selcted language.
  const isShowVerticalServiceFlow =
    isVerticalTop &&
    isFirstPage &&
    jobTypeVerticalServiceFlowSteps.length > 0 &&
    verticalServiceFlowData?.supportedLanguages.includes(language);

  const handlePillFilterChange = useCallback(
    ({ filterType, filterId, filterValue }: UpdateFiltersParams) => {
      dispatch(
        updateFiltersAndListNewPros(
          { filterType, filterId, filterValue },
          baseLandingPageProParameters
        )
      );
    },
    [baseLandingPageProParameters, dispatch]
  );

  const mustOpenRequestModal =
    (utmParameter.utmCampaign ?? '').includes('request-form') || requestFormQueryParam === 'true';

  const { hasVisitedSocialField } = useSocialRequestContinuation(jobTypeId);

  useEffect(() => {
    if (mustOpenRequestModal || hasVisitedSocialField) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        await openNewRequestModal({
          dispatch,
          properties: {
            jobTypeId,
          },
        });
      })();
    }
  }, [dispatch, jobTypeId, mustOpenRequestModal, hasVisitedSocialField]);

  const proCount = useSelector((state) => proCountSelector(state as RootState));

  useEffect(() => {
    if (proCount === 0) {
      handleNoProResult();
      handlePillFilterChange({ filterType: 'removeAll', filterId: '', filterValue: [] });
    }
  }, [handlePillFilterChange, proCount]);

  const pillData = useMemo(() => {
    const { asPath } = router;
    // Add default base URL for Chromatic
    const { searchParams } = new URL(asPath, process.env.DOMAIN ?? 'https://zehitomo.com');

    const dayUrlVal = searchParams.get('day') ?? '';
    const timeUrlVal = searchParams.get('time') ?? '';
    // HANDLE DAY TIME PILL
    const pills = [];
    if (jobType.filterSettings?.day || jobType.filterSettings?.time) {
      const value = getUrlDayTimeData(dayUrlVal, timeUrlVal);
      pills.push(value);
    }

    if (jobType.pricingFilterRange && jobType.pricingFilterRange.length > 0) {
      pills.push(
        ...setupPricingFilters(
          jobType,
          getQueryParamFromUrlSearchParamsForKey(searchParams, PRICE_RANGE_QUERY_PARAM_KEY),
          handlePriceRangeFilterValueChanged,
          t
        )
      );
    }

    // HANDLE MATCHING CRITERIA
    const matchingCriteriaToggles = setupMatchingCriteriaFilterToggleItems(
      jobType,
      getQueryParamFromUrlSearchParamsForKey(searchParams, MATCHING_CRITERIA_QUERY_PARAM_KEY)
    );

    pills.push(...matchingCriteriaToggles);
    return pills;
  }, [jobType, router, handlePriceRangeFilterValueChanged, t]);

  const groupProCount = useSelector((state) => proCountGroupSelector(state as RootState));

  const groupCityTitle = sprintf(translation.prosGroupTitle, {
    city: city?.name ?? '',
  });

  const shouldShowFilterSection = !isVerticalTop && isBottomAreaShown && pillData.length > 0;
  return (
    <>
      {/* FIXME: `translationId` does not exists in props of BannerZipcode */}
      <BannerZipcode
        bgImg={jobType.imageUrl ?? ''}
        city={city}
        cityGroupName={cityGroupName}
        jobType={jobType}
        prefecture={prefecture}
        translationId={pageTranslationId}
        testVariant={testVariant}
      />

      <div className={styles['breadcrumbs']}>
        <Breadcrumbs
          align="center"
          crumbs={
            proPageCount === 1
              ? crumbs
              : [
                  ...crumbs,
                  {
                    link: `${
                      crumbs.length > 0 ? crumbs[crumbs.length - 1].link : ''
                    }?page=${proPageCount}`,
                    text: language === 'en' ? `Page ${proPageCount}` : `${proPageCount}ページ目`,
                  },
                ]
          }
        />
      </div>

      {isVerticalTop &&
        isFirstPage &&
        (summary ||
          (CTTableInfo && costAndTimeInfoPosition === COST_AND_TIME_INFO_POSITION_UNDER_SUMMARY)) &&
        isBottomAreaShown && (
          <div className={styles['section']}>
            <div className={styles['content']}>
              {summary && <div dangerouslySetInnerHTML={{ __html: summary }} />}
              {CTTableInfo && costAndTimeInfoPosition && (
                <CostAndTime
                  CTTableInfo={CTTableInfo}
                  costAndTimeInfoPosition={costAndTimeInfoPosition}
                  costAndTimeInfoSummary={costAndTimeInfoSummary}
                  shouldDisplayCTInfoSummary={shouldDisplayCTInfoSummary}
                />
              )}
            </div>
          </div>
        )}

      {shouldShowVerticalHaveVerticalProQuestions &&
        verticalProQuestionAndAnswerData &&
        isBottomAreaShown && (
          <div className={styles['section-dark']}>
            <div className={styles['content']}>
              <h2 className={styles['vertical-pro-heading']}>
                {t('marketingPages:verticalProQuestionAndAnswer.sectionHeader', {
                  jobTypeDisplayName: jobTypeName,
                })}
              </h2>
              <VerticalProQuestionAndAnswers {...verticalProQuestionAndAnswerData} />
            </div>
          </div>
        )}

      {isShowVerticalServiceFlow && isBottomAreaShown && (
        <div className={styles['section']}>
          <VerticalServiceFlow
            sectionTitle={t('marketingPages:verticalServiceFlowSection.title', {
              jobType: jobTypeName,
            })}
            verticalServiceFlowSteps={jobTypeVerticalServiceFlowSteps}
          />
        </div>
      )}

      {isVerticalTop &&
        isFirstPage &&
        CTTableInfo &&
        isBottomAreaShown &&
        costAndTimeInfoPosition === COST_AND_TIME_INFO_POSITION_ABOVE_FINDPROGROUP && (
          <CostAndTime
            CTTableInfo={CTTableInfo}
            costAndTimeInfoPosition={costAndTimeInfoPosition}
            costAndTimeInfoSummary={costAndTimeInfoSummary}
            shouldDisplayCTInfoSummary={shouldDisplayCTInfoSummary}
          />
        )}

      {((isVerticalTop && isFirstPage) || !isBottomAreaShown) && (
        <div className={styles['section-dark']}>
          <div className={styles['content']}>
            <FindProGroup content={jobType.seo?.landingPage?.howToSearchAPro} />
          </div>
        </div>
      )}

      {isVerticalTop && totalCaseStudies > 0 && isBottomAreaShown && (
        <div className={styles['section-dark']}>
          <ListCaseStudies
            jobType={jobType}
            translationId={pageTranslationId}
            currentPage={currentCaseStudiesPage}
            onLoadMore={handleLoadMoreCaseStudies}
            onCurrentPageChange={onCaseStudiesPageChange}
          />
        </div>
      )}
      {(isVerticalTop || !isBottomAreaShown) && (
        <div className={styles['section']}>
          <Supporters />
        </div>
      )}

      {isBottomAreaShown && (
        <>
          <h2
            className={classNames(
              styles['section'],
              styles['job-type-heading'],
              styles['job-type-heading-vertical-top']
            )}
          >
            {title}
          </h2>
          {shouldShowFilterSection && (
            <>
              {/* Used as a scrollTarget right above the filters element. This scrolls up past the potential
              "sticky" state the filters can be in */}
              <div ref={filtersFocusElement} />
              <div
                className={classNames(styles['section'], styles['pill-section'], {
                  [styles['section-sticky']]: isSticky,
                })}
                ref={stickyRef}
              >
                <div className={styles['pill-filter-container']}>
                  <PillFilters
                    jobType={jobType}
                    onPillFilterChange={handlePillFilterChange}
                    onPillFilterClick={handlePillFilterClicked}
                    pillFiltersData={pillData}
                  />
                </div>
              </div>
            </>
          )}
          {/* TODO(micah): This element does not exist on non top vertical pages. After release determine
            if there will be a new iteration of this for non top vertical pages */}
          {isVerticalTop && isStartOnlineRequestShown && (
            <div className={styles['section-dark']}>
              <div className={classNames(styles['content'], styles['btn-container'])}>
                <Button
                  shadow={true}
                  fullWidth={true}
                  dataTestId="start-new-request"
                  onClick={openRequestModalForRemoteJobs}
                >
                  {t('marketingPages:requestFormEntryForm.findOnlinePro', {
                    jobType: jobType.translations.requestType,
                  })}
                </Button>
              </div>
            </div>
          )}
          <div
            className={classNames(styles['section-dark'], styles['list-pros-section'])}
            id="pros"
          >
            <ListProsContainer
              city={city}
              currentPage={proPageCount}
              landingPageFocusElement={filtersFocusElement}
              jobType={jobType}
              onLoadMore={handleLoadPros}
              prefecture={prefecture}
              translationId={pageTranslationId}
              updatePageNumber={updateProPageCount}
              numOfProsPerPage={numOfCityPros}
              cityGroupName={groupPageTitle}
              isGroupCity={false}
              isReviewsQAEnabled={featureFlags.isReviewsQAEnabled}
            />
            {isGroupCity && groupProCount > 0 && (
              <>
                <h2
                  className={classNames(
                    styles['section'],
                    styles['job-type-heading'],
                    styles['job-type-heading-vertical-top']
                  )}
                >
                  {groupCityTitle}
                </h2>
                <ListProsContainer
                  city={city}
                  currentPage={1}
                  jobType={jobType}
                  landingPageFocusElement={filtersFocusElement}
                  onLoadMore={handleLoadProsGroup}
                  prefecture={prefecture}
                  translationId={pageTranslationId}
                  updatePageNumber={updateProPageCount}
                  numOfProsPerPage={numOfGroupPros}
                  cityGroupName={''}
                  isGroupCity={true}
                  isReviewsQAEnabled={featureFlags.isReviewsQAEnabled}
                />
              </>
            )}
            <div className={styles['content']}>
              <ProCTALink eventType={'verticalProCTAClicked'} eventLabel={'vert'} />
            </div>
            {isGroupCity && groupProCount > 0 && groupLink && cityName && (
              <div className={styles['content']}>
                <div className={styles['btn-container']}>
                  <a href={groupLink} className={styles['go-to-city-btn']}>
                    {language === 'en'
                      ? `Find a pro nearby ${cityName} >`
                      : `${cityName}周辺のプロを探す `}
                    <IconRight height={24} width={24} />
                  </a>
                </div>
              </div>
            )}
          </div>
          {!isVerticalTop && (
            <div className={classNames(styles['section'], styles['supporters-footer'])}>
              <Supporters />
            </div>
          )}
          {newestReviews.length > 0 && (
            <div className={styles['section']} id="reviews">
              <div className={styles['content']}>
                <h2>{t('marketingPages:newestReviewSectionTitle')}</h2>
                <ListRecentReviews
                  featureFlags={featureFlags}
                  jobTypeId={jobTypeId}
                  newestReviews={newestReviews}
                />
              </div>
            </div>
          )}

          {isVerticalTop && proBlogPosts.length > 0 && (
            <div className={styles['section-dark']}>
              <div className={styles.content}>
                <div className={styles['title-with-button']}>
                  <h2>{t('marketingPages:proBlogPostSection.title')}</h2>
                  <Button onClick={handleProBlogButton}>
                    {t('marketingPages:proBlogPostSection.postButton')}
                  </Button>
                </div>
                <ProBlogPostList posts={proBlogPosts} />
              </div>
            </div>
          )}

          {popularRequests.length > 0 && (
            <div className={styles['section']}>
              <VerticalPopularRequest
                popularRequests={popularRequests}
                isReviewsQAEnabled={featureFlags.isReviewsQAEnabled}
              />
            </div>
          )}

          {isVerticalTop &&
            isFirstPage &&
            CTTableInfo &&
            costAndTimeInfoPosition === COST_AND_TIME_INFO_POSITION_ABOVE_VERTICALFAQ && (
              <CostAndTime
                CTTableInfo={CTTableInfo}
                costAndTimeInfoPosition={costAndTimeInfoPosition}
                costAndTimeInfoSummary={costAndTimeInfoSummary}
                shouldDisplayCTInfoSummary={shouldDisplayCTInfoSummary}
              />
            )}

          {isVerticalTop && isFirstPage && faqs && (
            <div className={styles['section']}>
              <div className={styles['content']}>
                <VerticalFAQ
                  faqs={faqs}
                  title={t('marketingPages:faqSectionTitle', {
                    jobType: jobTypeName,
                  })}
                />
              </div>
            </div>
          )}
          {journalGroupPosts.length > 0 && (
            <div className={styles['section-dark']}>
              <div className={styles.content}>
                <h2>
                  {t('marketingPages:latestArticleSectionTitle', {
                    jobType: jobTypeName,
                  })}
                </h2>
                <JournalGroup posts={journalGroupPosts} />
              </div>
            </div>
          )}
          <div className={styles['section']} ref={lastContainerRef}>
            <div className={styles['content']}>
              <InternalLinkGroups groups={groups} />
            </div>
          </div>

          <BannerSticky lastContainer={lastContainerRef} jobType={jobType} />
        </>
      )}
    </>
  );
};

export default LandingPageDefault;
export type { Props as LandingPageDefaultProps };
